import sparkle from '@/assets/svgs/icons/sparkle.svg';
import { classNames } from '@/utils';
import { AssistantAgentSession, VariantPrompt } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import {
  ArrowRight,
  EditPencil,
  GitFork,
  NavArrowDown,
  NavArrowUp,
  Settings,
  Trash,
} from 'iconoir-react';
import { useState } from 'react';

export interface DynamicPageDraftCardProps {
  /** Structure data from AssistantAgentSession containing dynamic page and variants */
  structureData: AssistantAgentSession['structureData'];
  /** Whether the card is initially expanded */
  defaultExpanded?: boolean;
  /** Callback when generate is clicked for a variant */
  onGenerate?: (variantId: string, prompt?: VariantPrompt) => void;
  /** Callback when edit (branch/fork) is clicked for a variant */
  onEdit?: (variantId: string) => void;
  /** Callback when edit constraints (smart traffic) is clicked for a variant */
  onEditConstraints?: (variantId: string) => void;
  /** Callback when delete is clicked for a variant */
  onDelete?: (variantId: string) => void;
  /** Callback when settings is clicked */
  onSettings?: () => void;
  /** Callback when "Go to dynamic page" is clicked */
  onGoToPage?: () => void;
  /** Footer message */
  footerMessage?: string;
  /** Disabled state for actions */
  disabled?: boolean;
  /** ID of the variant currently being generated */
  generatingVariantId?: string;
}

export function DynamicPageDraftCard({
  structureData,
  defaultExpanded = true,
  onGenerate,
  onEdit,
  onEditConstraints,
  onDelete,
  onSettings,
  onGoToPage,
  footerMessage = 'Page created. You can continue with other variants later.',
  disabled,
  generatingVariantId,
}: DynamicPageDraftCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [hoveredVariantId, setHoveredVariantId] = useState<string | null>(null);

  const { dynamicPage, variants } = structureData || {
    dynamicPage: {},
    variants: [],
  };
  const pageName = dynamicPage.name || 'Untitled Page';
  const pagePath = dynamicPage.path || '';
  const hasVariants = variants.length > 0;
  const dpCreated = structureData?.status === 'saved';

  return (
    <div className="pt-2 pb-4">
      <div
        className="flex flex-col rounded-xl border border-white bg-white px-2"
        style={{ boxShadow: '0px 1px 2px 0 rgba(21,28,36,0.05)' }}
      >
        {/* Header */}
        <div className={classNames('flex items-center justify-between py-2')}>
          <div className="flex items-center gap-1">
            {/* Expand/Collapse Button */}
            {hasVariants && (
              <Button
                size="icon-lg"
                variant="ghost"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <NavArrowUp
                    className="text-base-900 size-5"
                    strokeWidth={2}
                  />
                ) : (
                  <NavArrowDown
                    className="text-base-900 size-5"
                    strokeWidth={2}
                  />
                )}
              </Button>
            )}

            {/* Page name and path */}
            <div className="flex items-start gap-1">
              <span className="text-base-900 text-sm font-medium">
                {pageName}
              </span>
              <span className="text-base-500 text-sm font-medium">
                {pagePath}
              </span>
            </div>
          </div>

          {/* Settings button */}
          {onSettings && (
            <Tooltip>
              <TooltipTrigger>
                <div>
                  <Button
                    size="icon-lg"
                    variant="ghost"
                    onClick={onSettings}
                    disabled={disabled || dpCreated}
                  >
                    <Settings className="size-4" />
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">
                {dpCreated
                  ? 'Go to page to edit page settings'
                  : 'Page settings'}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Variants list - collapsible */}
        {hasVariants && (
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              isExpanded
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <Separator />

              <div className="flex max-h-60 flex-col gap-1 overflow-scroll py-2">
                {variants.map((variant) => {
                  const isHovered = hoveredVariantId === variant.id;
                  const isGenerating = generatingVariantId === variant.id;
                  const isGenerated = variant.status === 'generated';
                  const dpCreated = structureData?.status === 'saved';

                  const hasConstraints = variant.constraints
                    ? Object.entries(variant.constraints).some(
                        ([, v]) => Array.isArray(v) && v.length > 0,
                      )
                    : false;

                  return (
                    <div
                      key={variant.id}
                      className={`relative flex items-center justify-between gap-4 rounded-lg py-1.5 pr-1 pl-2 transition-colors ${
                        isHovered || isGenerating
                          ? 'bg-base-100'
                          : 'hover:bg-base-50'
                      }`}
                      onMouseEnter={() => setHoveredVariantId(variant.id)}
                      onMouseLeave={() => setHoveredVariantId(null)}
                    >
                      {/* Variant info */}
                      <div
                        className={'flex max-w-10/12 min-w-0 flex-1 flex-col'}
                      >
                        <div className="flex items-center gap-[5px]">
                          <div className="flex w-3 shrink-0 items-center justify-center">
                            <img
                              src={sparkle}
                              alt="sparkle"
                              className="size-3"
                            />
                          </div>
                          <span className="text-base-900 truncate text-sm">
                            {variant.name}
                          </span>
                          {variant.path && (
                            <span className="text-base-400 truncate text-xs">
                              /v/{variant.path}
                            </span>
                          )}
                        </div>
                        {variant.description && (
                          <p className="text-base-600 truncate pl-[17px] text-xs">
                            {variant.description}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex shrink-0 items-center gap-2">
                        {/* Show edit/delete buttons on hover for generated variants */}
                        {isHovered && (
                          <>
                            <div className="flex items-center -space-x-0.5">
                              {!dpCreated && onEdit && (
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon-lg"
                                      variant="ghost"
                                      className="hover:bg-base-200"
                                      onClick={() => onEdit(variant.id)}
                                      disabled={disabled}
                                    >
                                      <EditPencil className="text-base-900 size-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>Edit Variant</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {!dpCreated && onEditConstraints && (
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="hover:bg-base-200 relative"
                                      onClick={() =>
                                        onEditConstraints(variant.id)
                                      }
                                      disabled={disabled}
                                    >
                                      <GitFork className="text-base-900 size-4" />
                                      {/* Notification dot */}
                                      {hasConstraints && (
                                        <span className="border-base-100 bg-brand-500 absolute top-1 right-1 size-1.5 rounded-full border-[1.5px]" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>Edit Smart Traffic</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {!dpCreated && onDelete && (
                                <Tooltip delayDuration={200}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="hover:bg-base-200"
                                      onClick={() => onDelete(variant.id)}
                                      disabled={disabled}
                                    >
                                      <Trash className="text-base-900 size-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    <p>Delete variant</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            {!dpCreated && (
                              <Separator
                                orientation="vertical"
                                className="h-5"
                              />
                            )}
                          </>
                        )}

                        {/* Status badge or Generate button */}
                        {isGenerating ? (
                          <div className="flex items-center justify-center px-2">
                            <div className="bg-brand-100 flex items-center gap-2.5 rounded px-1.5 py-0.5">
                              <span className="text-brand-500 text-xs">
                                Generating...
                              </span>
                            </div>
                          </div>
                        ) : isGenerated ? (
                          <div className="flex items-center justify-center px-2">
                            <div
                              className={`flex items-center gap-2.5 rounded px-1.5 py-0.5 ${
                                isHovered ? 'bg-brand-100' : 'bg-brand-50'
                              }`}
                            >
                              <span className="text-brand-500 text-xs">
                                Generated
                              </span>
                            </div>
                          </div>
                        ) : (
                          <Tooltip disableHoverableContent>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className={classNames(
                                  'hover:bg-base-200',
                                  isHovered
                                    ? 'text-brand-500'
                                    : 'text-base-900 bg-white',
                                )}
                                onClick={() =>
                                  onGenerate?.(variant.id, variant.prompt)
                                }
                                disabled={disabled}
                              >
                                <span className="text-xs">Generate</span>
                                <ArrowRight className="size-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {!dpCreated
                                ? 'Approve the structure & start generating the first variant'
                                : 'Generate content for this variant'}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {structureData?.status === 'saved' && (
          <>
            <Separator />
            <div className="flex items-center justify-between pt-1.5 pb-1.5 pl-2">
              <p className="text-base-500 text-xs">{footerMessage}</p>

              <Button
                variant="ghost"
                size="sm"
                onClick={onGoToPage}
                disabled={disabled}
                className="text-xs"
              >
                Go to dynamic page
                <ArrowRight className="text-base-800 size-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
